// web/src/player/RoastPlayer.tsx
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import type { RoastPlayerProps, RoastPlayerHandle, VisibleWord, CharacterHandle, ZoomResult } from "./types";
import type { DetectedObject } from "../types/roast";
import { CaptionEngine } from "./captionEngine";
import { HighlightGate } from "./highlightGate";
import { buildBleeps, isCensoredAt, Bleeper, CENSOR } from "./censor";
import { buildZoomWindows, computeZoom } from "./smartZoom";
import { getCurrentSlide, getSlideForWord, buildSlideZoomWindows, wordsInDwell } from "./slideShow";
import { containRect } from "./geometry";
import { LipSyncAnalyser, MOUTH_RANGE } from "./lipSync";
import { EmotionEngine } from "./emotion";
import { doodleRevealTime } from "./doodle";
import { MusicBed } from "./musicBed";
import { SfxScheduler } from "./sfxScheduler";
import { musicUrl, sfxUrl, stingerUrl } from "./audio/manifest";
import type { SoundEffectCue } from "./audio/types";
import { RoastCharacter } from "./RoastCharacter";
import { Captions } from "./Captions";
import { DebugBoxes } from "./DebugBoxes";
import { useDebugMode } from "./useDebugMode";
import { getCharacter } from "../config/characters";
import { stageStyle } from "./stageGeometry";
import { PlayerControls } from "./PlayerControls";
import { CaptureController, type CaptureSourceState } from "./capture/captureController";
import { createAudioTap, type AudioTap } from "./capture/audioTap";
import { aspectDims, easeInOut } from "./capture/compositor";
import type { FrameDims } from "./capture/compositor";
import { detectCaptureTier, pickMp4Mime, probeExportDims } from "./capture/support";
import { reconcileDialogueEnd, normalizeWordTimings, auditWordTimings } from "./wordTimingRepair";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE } from "../config/censorship";

// Post-dialogue music "button": after a NATURAL end, keep the music bed playing
// this long before fading it out. iOS uses a 3.0s outro; product wants ~2s.
const MUSIC_TAIL_MS = 2000;
// Photo-export capture tail: the recorder must outlive the music hold AND its 2.5s
// fade-out (musicBed FADE_OUT_DURATION), so the exported video ends on the smooth fade
// instead of cutting at full music volume the instant the hold expires.
const MUSIC_FADE_MS = 2500;
const CAPTURE_TAIL_MS = MUSIC_TAIL_MS + MUSIC_FADE_MS;
const SLIDE_ANIM_MS = 280;
const CLICKER_MIN_INTERVAL_MS = 280; // a sweep flips ~250ms/slide; pops closer than this are throttled
// Touch devices can't hover-reveal the auto-hidden controls — a stage tap "peeks" them
// for this long instead (any further tap refreshes the window).
const CONTROLS_PEEK_MS = 2500;
// Stable empty reference for deck debug regions: setState with the SAME ref lets React bail,
// so non-dwell ticks don't re-render the player each frame during a sweep.
const EMPTY_DECK_REGIONS: DetectedObject[] = [];

// Minimal shape of the Screen Wake Lock API's sentinel (lib.dom doesn't ship
// WakeLockSentinel in this TS target) — just enough to release() on teardown.
interface WakeLockSentinelLike { release(): Promise<void> }

export const RoastPlayer = forwardRef<RoastPlayerHandle, RoastPlayerProps>(function RoastPlayer(props, handleRef): JSX.Element {
  const { mode, audioUrl, imageUrl, wordTimings: rawWordTimings, detectedObjects, autoPlay, doodleImageUrl, aspect, audio, captureFirstPlay, onCaptureComplete, onCaptureStatusChange, captionMask = DEFAULT_CAPTION_MASK, bleepMode = DEFAULT_BLEEP_MODE, fullscreen = false, onToggleFullscreen } = props;

  // Ingest backstop: enforce the timing invariants (finite, sorted, start<end) the whole
  // player assumes — identity on already-clean arrays, so downstream memos don't churn.
  const wordTimings = useMemo(() => normalizeWordTimings(rawWordTimings), [rawWordTimings]);

  const audioRef = useRef<HTMLAudioElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const charRef = useRef<CharacterHandle>(null);
  const debugRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const doodleRef = useRef<HTMLImageElement>(null);

  // ── Video-export capture (opt-in via captureFirstPlay; tiered — WebCodecs Tier-A
  //    or MediaRecorder+MP4 Tier-B, gated by detectCaptureTier()) ──
  const captureRef = useRef<CaptureController | null>(null);
  // Dims the CURRENT capture run was armed at (Tier-B may be 720p). The tick reads
  // this — not aspectDims() — for exportZoom + the Rive resolution guard, so a 720p
  // run never gets a 1080p-scaled transform (spec finding F1).
  const captureDimsRef = useRef<FrameDims | null>(null);
  // Held for exactly the span a capture is armed — see acquireWakeLock/releaseWakeLock below.
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const audioTapRef = useRef<AudioTap | null>(null);
  const captureArmedRef = useRef(false);   // a capture is in flight this run
  const captureDoneRef = useRef(false);    // first playthrough already captured/aborted (no re-capture)
  const unsupportedEmittedRef = useRef(false); // guard: emit "unsupported" at most once per mount
  // Preloaded <img>s for layers not directly reffable (outgoing deck slide). Keyed by src.
  const imgCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const [debugActiveIndex, setDebugActiveIndex] = useState<number | null>(null);
  const debug = useDebugMode();
  const debugEnabledRef = useRef(false);
  const debugActiveIndexRef = useRef<number | null>(null);

  const lipRef = useRef<LipSyncAnalyser | null>(null);
  const bleeperRef = useRef<Bleeper | null>(null);
  const musicBedRef = useRef<MusicBed | null>(null);
  const musicTailRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sfxRef = useRef<SfxScheduler | null>(null);
  const audioSetupDoneRef = useRef(false);
  const rafRef = useRef<number>(0);
  const tailRafRef = useRef<number>(0);            // photo-export music-tail: rAF pushing the frozen frame
  const tailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // photo-export music-tail: authoritative finish timer
  const lastCaptureStateRef = useRef<CaptureSourceState | null>(null); // last composed frame, replayed during the tail

  // Deck mode: all three deck props present → follow slide schedule instead of single photo.
  // (Hoisted above the memos: the end-signal reconciliation below is deck-aware.)
  const isDeck = !!(props.keySlides && props.fillerImages && props.slideSchedule);
  const [duration, setDuration] = useState(0); // real decoded audio duration (onLoadedMetadata)

  const captionEngine = useMemo(() => new CaptionEngine(), []);
  const highlightGate = useMemo(() => new HighlightGate(), []);
  const emotionEngine = useMemo(() => new EmotionEngine(), []);
  const bleeps = useMemo(() => buildBleeps(wordTimings, bleepMode), [wordTimings, bleepMode]);
  const zoomWindows = useMemo(() => buildZoomWindows(detectedObjects, wordTimings), [detectedObjects, wordTimings]);

  // Where the END SIGNALS (outro state, completion stinger, caption blank) fire:
  // wordTimings[last].end reconciled against the real audio duration — STT routinely
  // undershoots the final word's end, which used to flip the character to its outro and
  // blank the captions while the voice was still talking (and the inverse overshoot
  // silently DROPPED the ending). Ref mirror for the play-start tick closure.
  const dialogueEndSec = useMemo(
    () => reconcileDialogueEnd(wordTimings, duration > 0 ? duration : undefined, isDeck),
    [wordTimings, duration, isDeck],
  );
  const dialogueEndRef = useRef(0);
  dialogueEndRef.current = dialogueEndSec;

  // Completion stinger appended to every cue list when audio is present and word timings exist.
  // Mirror of iOS: dialogue end triggers "Roasted"/"Toasted" stinger at 0.8 volume.
  const scheduledCues = useMemo<SoundEffectCue[]>(() => {
    if (!audio) return [];
    const base: SoundEffectCue[] = [...audio.cues];
    if (wordTimings.length > 0) {
      base.push({
        effectName: mode === "toast" ? "Toasted" : "Roasted",
        triggerTime: dialogueEndSec,
        volume: 0.8,
        category: "reactions",
      });
    }
    return base;
  }, [audio, wordTimings, mode, dialogueEndSec]);

  // Doodle reveal: compute once from word timings; null when no doodleImageUrl or empty timings.
  const revealTime = useMemo(
    () => (doodleImageUrl ? doodleRevealTime(wordTimings) : null),
    [doodleImageUrl, wordTimings],
  );
  // Ref guards against stale-closure reads inside tick; state drives the render.
  const doodleRevealedRef = useRef(false);
  const roastedFiredRef = useRef(false);
  const [doodleRevealed, setDoodleRevealed] = useState(false);

  // Deck per-slide doodle reveal: mirror the photo reveal, but scoped to the
  // CURRENT dwell. deckDwellRef caches the active dwell's identity + its clamped
  // reveal time so the tick closure re-derives them only when the dwell changes.
  const [deckDoodleUrl, setDeckDoodleUrl] = useState<string | null>(null);
  const [deckDoodleRevealed, setDeckDoodleRevealed] = useState(false);
  const deckDoodleRef = useRef<HTMLImageElement>(null);
  const deckDoodleRevealedRef = useRef(false);
  const deckDwellRef = useRef<{ slideIndex: number; startSec: number; revealSec: number } | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // Touch peek for the auto-hidden controls: taps make :hover STICK on mobile, so the
  // hover-reveal is gated to hover-capable devices (see the wrapper className) and touch
  // gets an explicit tap-to-peek with an auto-hide timer instead.
  const [controlsPeek, setControlsPeek] = useState(false);
  const controlsPeekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekControls = (): void => {
    setControlsPeek(true);
    if (controlsPeekTimerRef.current !== null) clearTimeout(controlsPeekTimerRef.current);
    controlsPeekTimerRef.current = setTimeout(() => {
      setControlsPeek(false);
      controlsPeekTimerRef.current = null;
    }, CONTROLS_PEEK_MS);
  };

  const playCycleRef = useRef(0);                 // guards late-joining asset loaders against pause/replay races
  const audibleWordIndexRef = useRef(-1);         // last word actually heard (gated); drives the deck slide
  const lastClickedDeckUrlRef = useRef<string>(""); // (used by the Task 5 clicker; armed here)
  const lastClickAdvanceRef = useRef<number>(-Infinity); // wall-clock of the last real slide advance; throttles the sweep clicker

  // Deck slide-out animation: track the outgoing (previous) slide URL so we can
  // render a one-shot overlay that slides off to the left, revealing the new slide.
  const prevDeckUrlRef = useRef<string>("");
  const lastSlideChangeRef = useRef<number>(0);
  const [outgoingDeckUrl, setOutgoingDeckUrl] = useState<string | null>(null);
  // Ref mirror of outgoingDeckUrl: the rAF tick closure is created once at play-start,
  // so it can't read changing useState — the capture path reads this ref instead.
  const outgoingDeckUrlRef = useRef<string | null>(null);
  const [visibleWords, setVisibleWords] = useState<VisibleWord[]>([]);
  // Gate the caption highlight to actual playback: STT timings have no silence
  // gaps, so a word's `start` can lead its audible onset by up to ~1s. Censored
  // segments are ducked to near-silence but ARE being spoken, so count as sound.
  const [captionHighlight, setCaptionHighlight] = useState(true);
  const riveSrc = getCharacter(mode).riveSource;

  const slideImage = (idx: number): string =>
    props.keySlides!.find((s) => s.slideIndex === idx)?.imageUrl
    ?? props.fillerImages!.find((s) => s.slideIndex === idx)?.imageUrl
    ?? "";
  const [deckImageUrl, setDeckImageUrl] = useState<string>(() =>
    isDeck && props.slideSchedule!.length ? slideImage(props.slideSchedule![0].slideIndex) : "");
  const [deckDebugRegions, setDeckDebugRegions] = useState<DetectedObject[]>([]);

  useEffect(() => { captionEngine.setup(wordTimings); }, [captionEngine, wordTimings]);
  // Narrower aspects (9:16) show fewer words so the caption stays on ONE line (wide 16:9 fits 5).
  useEffect(() => { captionEngine.setWindowSize(aspect === "9:16" ? 3 : 5); }, [captionEngine, aspect]);
  // Caption blank waits for the reconciled dialogue end (not a possibly-undershot last.end).
  useEffect(() => {
    captionEngine.setCompletionSec(dialogueEndSec > 0 ? dialogueEndSec : null);
  }, [captionEngine, dialogueEndSec]);
  // Diagnostics breadcrumb (no behavior change): once metadata gives us the real duration,
  // log invariant violations in the RAW server timings so intermittent "odd playback"
  // reports come with console evidence.
  useEffect(() => {
    if (duration <= 0) return;
    for (const w of auditWordTimings(rawWordTimings, { durationSec: duration, isDeck })) {
      console.warn("[timings]", w);
    }
  }, [rawWordTimings, duration, isDeck]);

  // Capture-only image cache: returns a loaded <img> for a url (used to composite the
  // OUTGOING deck slide during a transition, which has no stable DOM ref). Returns null
  // until decoded so the compositor cleanly skips it.
  function getCachedImg(url: string): HTMLImageElement | null {
    if (!url) return null;
    const cache = imgCacheRef.current;
    let img = cache.get(url);
    if (!img) { img = new Image(); img.src = url; cache.set(url, img); }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }

  // Preload deck slide images so the slide-out overlay is decoded before capture composites it.
  useEffect(() => {
    if (!captureFirstPlay || !isDeck) return;
    const urls = [
      ...(props.keySlides ?? []).map((s) => s.imageUrl),
      ...(props.fillerImages ?? []).map((s) => s.imageUrl),
    ];
    for (const u of urls) if (u) getCachedImg(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureFirstPlay, props.keySlides, props.fillerImages]);

  // Warm-preload the (≤3) key-slide doodle overlays as soon as keySlides is known — NOT
  // gated to captureFirstPlay, since this also avoids a blank-then-pop on ordinary
  // playback: the doodle <img> mounts fresh at dwell start and, for a short dwell (or the
  // Pass-3 fallback clamp), may not be decoded yet when its reveal fires.
  useEffect(() => {
    if (!isDeck) return;
    for (const s of props.keySlides ?? []) if (s.doodleImageUrl) getCachedImg(s.doodleImageUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeck, props.keySlides]);

  // Keep the at-rest deck backdrop on the first scheduled slide when deck props
  // arrive or change after mount (e.g. a fresh artifact swapped into the preview,
  // or async-loaded replay). tick() drives it during playback.
  useEffect(() => {
    if (!isDeck || !props.slideSchedule!.length) return;
    const url = slideImage(props.slideSchedule![0].slideIndex);
    setDeckImageUrl((prev) => (prev === url ? prev : url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeck, props.slideSchedule, props.keySlides, props.fillerImages]);

  // Slide-out animation: when deckImageUrl changes, stage the outgoing slide as an overlay
  // that slides off to the left. Skip animation for rapid flips (fast riffle) to avoid smear.
  useEffect(() => {
    if (!isDeck) return;
    const prev = prevDeckUrlRef.current;
    prevDeckUrlRef.current = deckImageUrl;
    if (!prev || prev === deckImageUrl) return;
    const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const sinceLast = now - lastSlideChangeRef.current;
    lastSlideChangeRef.current = now;
    if (sinceLast < SLIDE_ANIM_MS) { setOutgoingDeckUrl(null); outgoingDeckUrlRef.current = null; return; } // rapid flip → instant cut
    setOutgoingDeckUrl(prev);
    outgoingDeckUrlRef.current = prev; // mirror for the capture tick closure
  }, [deckImageUrl, isDeck]);

  function positionDebugLayer() {
    const img = imgRef.current;
    const dbg = debugRef.current;
    if (!img || !dbg) return;
    const { offX, offY, dispW, dispH } = containRect(
      img.clientWidth, img.clientHeight, img.naturalWidth, img.naturalHeight,
    );
    dbg.style.left = `${offX}px`;
    dbg.style.top = `${offY}px`;
    dbg.style.width = `${dispW}px`;
    dbg.style.height = `${dispH}px`;
  }

  function applyImgTransform(z: ZoomResult | null) {
    const img = imgRef.current;
    if (img) {
      if (z) {
        img.style.objectFit = "contain";
        img.style.transformOrigin = z.transformOrigin;
        img.style.transition = "none";
        img.style.transform = z.transform;
      } else {
        img.style.objectFit = "contain"; // baseline is always extents (full image, aspect-fit)
        img.style.transform = "none";
      }
    }
    // Mirror the photo's transform onto the debug overlay so boxes track the zoom.
    const dbg = debugRef.current;
    if (dbg) {
      dbg.style.transformOrigin = z ? z.transformOrigin : "center center";
      dbg.style.transform = z ? z.transform : "none";
    }
  }

  function tick() {
    const audioEl = audioRef.current;
    const lip = lipRef.current;
    const char = charRef.current;
    const bleeper = bleeperRef.current;
    if (!audioEl || !lip || !char) return;

    const t = audioEl.currentTime;
    setCurrentTime(t);

    const closed = MOUTH_RANGE[mode].closed;
    const mouth = lip.mouthValue(mode);
    char.setMouth(mouth);

    const censored = isCensoredAt(t, bleeps);
    char.setCensor(censored);
    if (bleeper) {
      if (censored && !bleeper.isActive) { bleeper.start(); lip.setDuck(CENSOR.DUCK_GAIN); }
      if (!censored && bleeper.isActive) { bleeper.stop(); lip.setDuck(1.0); }
    }

    // Caption highlight gate + audible word index (the deck slides slave to this).
    const words = captionEngine.update(t);
    const currentIndex = words.find((w) => w.state === "current")?.index ?? -1;
    const lit = highlightGate.update(t, lip.isSpeaking || censored, currentIndex);
    if (lit && currentIndex >= 0) audibleWordIndexRef.current = currentIndex; // else HOLD last spoken word
    setCaptionHighlight(lit);
    setVisibleWords(words);

    // Deck slide selection: word path (audible index) or time path (interpolated/legacy).
    let cur = null as ReturnType<typeof getCurrentSlide>;
    if (isDeck) {
      const sched = props.slideSchedule!;
      const wordAligned = sched.length > 0 && sched[0].startWord !== undefined;
      cur = wordAligned ? getSlideForWord(audibleWordIndexRef.current, sched) : getCurrentSlide(t, sched);
      if (cur) {
        const url = slideImage(cur.slideIndex);
        if (url) {
          setDeckImageUrl((prev) => (prev === url ? prev : url));
          if (url !== lastClickedDeckUrlRef.current) {
            lastClickedDeckUrlRef.current = url;
            // Throttle the clicker during a fast sweep: only pop when this advance
            // follows a calm gap (>= CLICKER_MIN_INTERVAL_MS since the previous
            // advance). Rapid riffle flips (sub-interval) stay silent so a sweep
            // doesn't machine-gun the pop; welded to the visual flip otherwise.
            const nowMs = (typeof performance !== "undefined" ? performance.now() : Date.now());
            const sinceLastAdvance = nowMs - lastClickAdvanceRef.current;
            lastClickAdvanceRef.current = nowMs;
            if (sinceLastAdvance >= CLICKER_MIN_INTERVAL_MS) {
              sfxRef.current?.playNow("pop", 0.3);
            }
          }
        }
      }
    }

    const vpW = imgRef.current?.clientWidth ?? 0;
    const vpH = imgRef.current?.clientHeight ?? 0;
    const imgNatW = imgRef.current?.naturalWidth ?? 0;
    const imgNatH = imgRef.current?.naturalHeight ?? 0;

    // For capture we recompute the zoom at the EXPORT resolution (computeZoom is
    // viewport-parameterized, so this is the exact same transform at 1920×1080 /
    // 1080×1920 — zero drift vs the on-screen transform).
    const capturing = !!captureRef.current;
    const capDims = capturing ? (captureDimsRef.current ?? aspectDims(aspect)) : null;
    let exportZoom: ZoomResult | null = null;

    let zoom: ZoomResult | null = null;
    let deckDoodleOpacity = 0;
    // Freshly re-derived (not read from the `deckDoodleUrl` state, which is stale inside
    // this tick closure — see the file-level note on tick's stale-closure reads) URL the
    // deck-doodle overlay OUGHT to show right now. Used by the capture guard below.
    let deckDoodleExpectedUrl: string | undefined;
    if (isDeck) {
      const dwellKey = cur && cur.kind === "dwell" ? props.keySlides!.find((s) => s.slideIndex === cur!.slideIndex) : undefined;
      setDeckDebugRegions(dwellKey ? (dwellKey.detectedObjects ?? EMPTY_DECK_REGIONS) : EMPTY_DECK_REGIONS);

      // Per-dwell doodle reveal: when the current dwell slide carries a doodle,
      // latch a reveal a beat into the dwell (clamped to leave the 0.6s crossfade
      // room before the slide advances), then pull the zoom back to full extents.
      if (cur && cur.kind === "dwell" && dwellKey?.doodleImageUrl) {
        deckDoodleExpectedUrl = dwellKey.doodleImageUrl;
        if (deckDwellRef.current?.slideIndex !== cur.slideIndex || deckDwellRef.current.startSec !== cur.startSec) {
          const dwellWords = wordsInDwell(wordTimings, cur.startSec, cur.endSec);
          const raw = doodleRevealTime(dwellWords) ?? cur.endSec;
          const revealSec = Math.max(cur.startSec, Math.min(raw, cur.endSec - 0.6));
          deckDwellRef.current = { slideIndex: cur.slideIndex, startSec: cur.startSec, revealSec };
          setDeckDoodleUrl(dwellKey.doodleImageUrl);
          setDeckDoodleRevealed(false);
          deckDoodleRevealedRef.current = false;
        }
        const revealSec = deckDwellRef.current.revealSec;
        if (t >= revealSec) {
          if (!deckDoodleRevealedRef.current) {
            deckDoodleRevealedRef.current = true;
            setDeckDoodleRevealed(true);
          }
          // ease-in-out matches the on-screen CSS `transition: opacity 0.6s ease-in-out`.
          deckDoodleOpacity = easeInOut(Math.min(1, Math.max(0, (t - revealSec) / 0.6)));
        }
      } else if (deckDwellRef.current) {
        // Left the doodled dwell (a sweep, or a doodle-less dwell): clear the reveal.
        deckDwellRef.current = null;
        setDeckDoodleUrl(null);
        setDeckDoodleRevealed(false);
        deckDoodleRevealedRef.current = false;
      }

      // Once the doodle has revealed, pull the zoom back to full extents (skip computeZoom).
      const doodlePulled = !!(deckDwellRef.current && t >= deckDwellRef.current.revealSec);
      if (cur && cur.kind === "dwell" && !cur.zoomDisabled && !doodlePulled && dwellKey && dwellKey.detectedObjects.length) {
        // Time path: scope phrase→time matching to THIS dwell's word slice (disambiguates a
        // repeated phrase) and let computeZoom decide WHEN within the dwell to push in.
        const dwellWords = wordsInDwell(wordTimings, cur.startSec, cur.endSec);
        const wins = buildSlideZoomWindows(dwellKey.detectedObjects, dwellWords, cur.startSec, cur.endSec);
        // A 9:16 paged source is a doc (portrait pages; higher-max, top-anchored zoom
        // profile) — a 16:9 paged source is a deck (wide low-density slides).
        const zoomProfile = aspect === "9:16" ? "doc" : "deck";
        zoom = computeZoom(t, wins, vpW, vpH, imgNatW, imgNatH, zoomProfile);
        if (capDims) exportZoom = computeZoom(t, wins, capDims.w, capDims.h, imgNatW, imgNatH, zoomProfile);
      }
      applyImgTransform(zoom);
    } else {
      const noZoom = revealTime !== null && t >= revealTime;
      zoom = noZoom ? null : computeZoom(t, zoomWindows, vpW, vpH, imgNatW, imgNatH);
      if (capDims) exportZoom = noZoom ? null : computeZoom(t, zoomWindows, capDims.w, capDims.h, imgNatW, imgNatH);
      applyImgTransform(zoom);
    }

    const activeWin = zoomWindows.find((x) => t >= x.startSec && t <= x.endSec);
    const activeIdx = activeWin ? detectedObjects.indexOf(activeWin.obj) : -1;
    const nextActive = activeIdx === -1 ? null : activeIdx; // keep the null contract (never -1)
    if (nextActive !== debugActiveIndexRef.current) {
      debugActiveIndexRef.current = nextActive;
      setDebugActiveIndex(nextActive);
    }

    char.setEmotion(emotionEngine.update(t, { zoomActive: !!zoom?.isActive, isCensored: censored, mouth, closed }));

    // Outro trigger: drive State enum once when dialogue ends, never again until replay.
    // The .riv State enum is {Intro, Roasted, Roasted Doodle} — shared across the
    // mrroast/mrtoast artboards. The artboard (selected by mode) renders the
    // roasted-vs-toasted look, so toast also uses "Roasted" (there is no "Toasted"
    // State value). Doodle is roast-only, so "Roasted Doodle" stays roast-gated.
    if (!roastedFiredRef.current && wordTimings.length > 0 && t >= dialogueEndRef.current) {
      roastedFiredRef.current = true;
      char.setState(doodleImageUrl && mode === "roast" ? "Roasted Doodle" : "Roasted");
    }

    // Doodle reveal: once the clock crosses revealTime, latch revealed (ref prevents re-firing).
    if (revealTime !== null && t >= revealTime && !doodleRevealedRef.current) {
      doodleRevealedRef.current = true;
      setDoodleRevealed(true);
    }

    // ── Video-export capture: composite this frame + push to the encoder. Fully
    //    guarded — a capture failure must NEVER disturb playback. ──
    const cap = captureRef.current;
    if (cap) {
      // Re-assert export resolution only if canvas width has drifted below target (rare resize/zoom event).
      // Rive's resizeDrawingSurfaceToCanvas is unconditional — it reallocates WebGL backing buffer on every call,
      // so we guard it to only fire when the canvas has actually been clobbered below export dims.
      try {
        if (capDims) {
          const cv = charRef.current?.getCanvas();
          if (!cv || cv.width < capDims.w) charRef.current?.setCaptureResolution(capDims);
        }
      } catch { /* ignore — never disturb playback */ }
      try {
        // Slide-out: read the REF (the tick closure can't see changing state), and only
        // composite while the 280ms sweep is in progress.
        const outUrl = isDeck ? outgoingDeckUrlRef.current : null;
        const slideOutProgress = Math.min(
          1,
          Math.max(0, ((typeof performance !== "undefined" ? performance.now() : Date.now()) - lastSlideChangeRef.current) / SLIDE_ANIM_MS),
        );
        const slideOutImg = outUrl && slideOutProgress < 1 ? getCachedImg(outUrl) : null;
        // bg: prefer the live <img>; fall back to the cached decode if it momentarily
        // reports naturalWidth 0 during a slide swap (prevents a black flash).
        const bgEl = imgRef.current;
        const bgSrc = bgEl?.currentSrc || bgEl?.src || "";
        const bgImg = bgEl && bgEl.naturalWidth ? (bgEl as CanvasImageSource) : getCachedImg(bgSrc);
        const doodleEl = doodleRef.current;
        // ease-in-out to match the on-screen CSS `transition: opacity 0.6s ease-in-out`.
        const doodleOpacity = revealTime !== null && t >= revealTime ? easeInOut(Math.min(1, Math.max(0, (t - revealTime) / 0.6))) : 0;
        // Guard against a one-frame stale ref: on a transition into a new doodled dwell,
        // setDeckDoodleUrl(newUrl) has run but React may not yet have committed the keyed
        // <img> remount, so deckDoodleRef.current can still be the PREVIOUS dwell's <img>.
        // Only composite once the mounted element's own src has caught up to what THIS
        // tick expects — otherwise drop the doodle for that one frame rather than
        // compositing the wrong image.
        const deckDoodleElReady = !!(
          deckDoodleRef.current &&
          deckDoodleOpacity > 0 &&
          deckDoodleRef.current.naturalWidth &&
          deckDoodleRef.current.getAttribute("src") === deckDoodleExpectedUrl
        );
        const state: CaptureSourceState = {
          bg: bgImg ? { el: bgImg, src: bgSrc } : null,
          zoom: exportZoom,
          slideOut: slideOutImg ? { img: slideOutImg, progress: slideOutProgress } : null,
          doodle: isDeck
            ? (deckDoodleElReady ? { img: deckDoodleRef.current!, opacity: deckDoodleOpacity } : null)
            : (doodleEl && doodleOpacity > 0 && doodleEl.naturalWidth ? { img: doodleEl, opacity: doodleOpacity } : null),
          rive: char.getCanvas(),
          captions: { words, mode, highlightActive: lit },
        };
        lastCaptureStateRef.current = state; // replayed during the photo-export music tail
        cap.tick(state, t);
      } catch { /* capture is best-effort; never break the watch */ }
    }

    if (!audioEl.paused) rafRef.current = requestAnimationFrame(tick);
  }

  function handlePlay() {
    const audioEl = audioRef.current!;
    const cycle = ++playCycleRef.current; // this play run's identity (loaders check it)

    // Capture is armed ONLY on the first play (when the audio graph is built), so the
    // additive tap can sit on the same graph and capture the full mix.
    const firstPlay = !lipRef.current;
    const wantCapture = firstPlay && !!captureFirstPlay && !captureDoneRef.current && detectCaptureTier() !== null;

    // Emit "unsupported" once per mount when the consumer wants capture but neither
    // capture tier (WebCodecs nor MediaRecorder+MP4) is available.
    // Guard with unsupportedEmittedRef so replays don't spam the callback.
    if (firstPlay && !!captureFirstPlay && detectCaptureTier() === null && !unsupportedEmittedRef.current) {
      unsupportedEmittedRef.current = true;
      try { onCaptureStatusChange?.("unsupported"); } catch { /* consumer error — ignore */ }
    }

    // ── 1. Build WebAudio graph (first play only) ─────────────────────────
    if (!lipRef.current) {
      const lip = new LipSyncAnalyser(audioEl);
      lipRef.current = lip;
      // Additive capture tap: parallel-connect the voice + route music/SFX/bleeper
      // through a unity bus that fans to BOTH speakers and the tap. Speaker output
      // stays byte-identical. Only created when capturing.
      if (wantCapture) {
        try {
          const tap = createAudioTap(lip.context);
          audioTapRef.current = tap;
          lip.connectTap(tap.streamDest);
        } catch { audioTapRef.current = null; }
      }
      const captureDest = audioTapRef.current ? audioTapRef.current.bus : lip.context.destination;
      const bleeper = new Bleeper(lip.context, captureDest);
      // Do NOT await: the watch must start immediately (prod networks made this await
      // eat the first seconds of captions/animation). Until the buffer lands, the tick's
      // bleeper.start() no-ops but the DUCK still engages — curses are muffled, not open.
      bleeperRef.current = bleeper;
      void bleeper.load().catch(() => { /* oscillator-free fallback: no asset -> no bleep tone */ });
    }

    // ── 2. Build music bed + SFX scheduler (first play with audio only) ──
    if (audio && !audioSetupDoneRef.current) {
      audioSetupDoneRef.current = true;
      const ctx = lipRef.current.context;
      // Route music + SFX through the capture bus when capturing (unity gain → speakers
      // unchanged), else straight to the destination (photo/deck audio byte-identical).
      const dest = audioTapRef.current ? audioTapRef.current.bus : ctx.destination;

      // resolveUrl: stinger names go to stingerUrl(mode); everything else → sfxUrl(name)
      const resolveUrl = (effectName: string): string =>
        effectName === "Roasted" || effectName === "Toasted"
          ? stingerUrl(mode)
          : sfxUrl(effectName);

      // Loads must NOT block the watch (prod networks: a multi-second music download used
      // to stall captions/animation/bleeps for the first ~5-8 words). Fire-and-forget:
      // each layer JOINS the live playback aligned to the audio clock when it lands
      // (MusicBed.start(offset) / SfxScheduler.schedule(cues, t) both align), unless a
      // newer play cycle or a pause supersedes this run.
      void (async () => {
        // Music bed: independent try/catch so a load failure cannot prevent SFX setup
        try {
          const bed = new MusicBed(ctx, dest);
          await bed.load(musicUrl(audio.musicTrack));
          musicBedRef.current = bed;
          if (playCycleRef.current === cycle) bed.start(audioEl.currentTime);
        } catch { /* music file unavailable — play without music */ }

        // SFX scheduler: independent try/catch; preload is internally error-tolerant per effect
        try {
          const scheduler = new SfxScheduler(ctx, dest);
          const preloadCues = isDeck
            ? [...scheduledCues, { effectName: "pop", triggerTime: 0, volume: 0.3, category: "transitions" as const }]
            : scheduledCues;
          await scheduler.preload(preloadCues, resolveUrl);  // preload only; schedule() still uses scheduledCues
          sfxRef.current = scheduler;
          if (playCycleRef.current === cycle) {
            scheduler.stopAll();
            scheduler.schedule(scheduledCues, audioEl.currentTime);
          }
        } catch { /* SFX unavailable — play without sound effects */ }
      })();
    }

    // ── 3. Engine resets for clean replay ────────────────────────────────
    captionEngine.reset();         // Fix 1: reset so replay doesn't stay blank
    highlightGate.reset();         // re-arm highlight gate for clean replay
    audibleWordIndexRef.current = -1;              // re-arm: nothing heard yet
    // Deck replay opening-pop fix: snap the backdrop + clicker baseline back to the
    // title slide so the opening tick (getCurrentSlide(0) → slide 0) is NOT treated
    // as a fresh advance (no pop), and a replay starts on the title rather than the
    // last slide left on screen. Photo mode keeps the old baseline.
    const deckTitleUrl = isDeck && props.slideSchedule!.length
      ? slideImage(props.slideSchedule![0].slideIndex)
      : "";
    if (isDeck) setDeckImageUrl(deckTitleUrl);
    lastClickedDeckUrlRef.current = isDeck ? deckTitleUrl : deckImageUrl;
    lastClickAdvanceRef.current = -Infinity;       // re-arm the clicker throttle clock for replay
    emotionEngine.reset();         // Fix 1: reset hold/censor state for clean replay
    doodleRevealedRef.current = false;   // reset so doodle re-reveals on replay
    setDoodleRevealed(false);
    deckDwellRef.current = null;          // reset per-dwell doodle reveal for replay
    setDeckDoodleUrl(null);
    setDeckDoodleRevealed(false);
    deckDoodleRevealedRef.current = false;
    roastedFiredRef.current = false;     // re-arm outro for replay
    charRef.current?.setState("Intro");  // restart character intro animation

    // ── 4. Start / re-align music + SFX to current voice clock (every play) ─
    // Cancel any pending end-of-roast music tail so a replay doesn't fade the
    // freshly-started music a moment later.
    if (musicTailRef.current !== null) {
      clearTimeout(musicTailRef.current);
      musicTailRef.current = null;
    }
    // If a photo-export capture tail from a prior end is still running, stop it and finalize
    // now (emit what we captured) — never carry a tail across a replay.
    if (tailTimerRef.current !== null) {
      stopCaptureTail();
      finalizeOrAbortCapture("finish");
    }
    if (musicBedRef.current) {
      musicBedRef.current.start(audioEl.currentTime);
    }
    if (sfxRef.current) {
      sfxRef.current.stopAll();  // cancel any previously scheduled nodes
      sfxRef.current.schedule(scheduledCues, audioEl.currentTime);
    }

    lipRef.current.resume();
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    // ── Arm the MP4 capture controller OFF the critical path. The watch (audio resume
    //    + rAF loop) has already started above; codec probing / font readiness must
    //    NEVER gate or delay playback. tick() no-ops until captureRef is set (misses at
    //    most a frame or two). Fully guarded — capture failure never disturbs the watch. ──
    if (wantCapture && audioTapRef.current && !captureRef.current && !captureArmedRef.current) {
      void armCaptureController();
    }
  }

  // Best-effort: keep the screen on while a capture is armed (a locked phone starves
  // rAF → frozen frames). Absent API / rejection are silently ignored; if the page
  // hides anyway, the capture is already dead via pause→abort.
  function acquireWakeLock() {
    try {
      const wl = (navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> } }).wakeLock;
      void wl?.request("screen").then((s) => {
        // The run may have finalized (fast pause/seek) or re-armed while request()
        // was in flight — a sentinel we can't own must be released on the spot or
        // the screen stays locked with no release site left.
        if (captureArmedRef.current && !wakeLockRef.current) {
          wakeLockRef.current = s;
        } else {
          void s.release().catch(() => {});
        }
      }).catch(() => {});
    } catch { /* best-effort */ }
  }
  function releaseWakeLock() {
    try { void wakeLockRef.current?.release().catch(() => {}); } catch { /* ignore */ }
    wakeLockRef.current = null;
  }

  // Finalize (natural end → encode + emit blob) or abort (pause/seek → discard) the
  // in-flight capture. Runs at most once per capture run (captureArmedRef gate). After
  // either path, captureDoneRef is set — re-arm is only possible via exportVideo().
  function finalizeOrAbortCapture(kind: "finish" | "abort") {
    const cap = captureRef.current;
    if (!cap || !captureArmedRef.current) {
      // No controller yet but an arm is in flight (armed ⇔ claimed at arm entry):
      // kill the claim so the resolving arm sees a dead run and aborts itself.
      if (!cap) captureArmedRef.current = false;
      return;
    }
    captureArmedRef.current = false;
    captureRef.current = null;
    captureDimsRef.current = null;
    releaseWakeLock();
    captureDoneRef.current = true;
    // Restore normal Rive drawing surface DPR now that capture is done.
    try { charRef.current?.setCaptureResolution(null); } catch { /* ignore */ }
    try {
      if (kind === "finish") {
        cap.finish()
          .then((blob) => {
            if (blob) {
              try { onCaptureComplete?.(blob); } catch { /* consumer error — ignore */ }
              try { onCaptureStatusChange?.("complete"); } catch { /* consumer error — ignore */ }
            } else {
              try { onCaptureStatusChange?.("aborted"); } catch { /* consumer error — ignore */ }
            }
          })
          .catch(() => { /* encode failure — no video, playback already fine */ });
      } else {
        cap.abort();
        try { onCaptureStatusChange?.("aborted"); } catch { /* consumer error — ignore */ }
      }
    } catch { /* never let capture teardown disturb playback */ }
  }

  // stopSfx=true  → user pause: kill everything immediately (music + sfx now).
  // stopSfx=false → natural end: let the already-scheduled stinger ring out AND
  //                 let the music play a ~2s "button" before fading. The fade is
  //                 deferred via musicTailRef and is cancelled by replay/pause/unmount.
  // Cancel the photo-export music tail (both the frame-pusher rAF and the finish timer).
  function stopCaptureTail() {
    if (tailRafRef.current) { cancelAnimationFrame(tailRafRef.current); tailRafRef.current = 0; }
    if (tailTimerRef.current !== null) { clearTimeout(tailTimerRef.current); tailTimerRef.current = null; }
  }

  // Photo export: keep the recorder running CAPTURE_TAIL_MS past the voice end so the audio
  // pump records the music "button" AND its full fade-out (and the character's outro, still
  // rendering on the live Rive canvas). An rAF pushes the frozen final frame (advancing
  // timestamps → the video track extends to match); a setTimeout is the AUTHORITATIVE finish
  // so the recorder always closes on time even if the tab is backgrounded (rAF suspends
  // while hidden, timers don't) — keeping audio/video durations matched. No-op if a tail is
  // already running.
  function runCaptureTail(cap: CaptureController, lastState: CaptureSourceState) {
    if (tailTimerRef.current !== null) return;
    const audioEl = audioRef.current;
    const baseSec = audioEl && isFinite(audioEl.duration) ? audioEl.duration : (audioEl?.currentTime ?? 0);
    const startMs = typeof performance !== "undefined" ? performance.now() : Date.now();
    const push = () => {
      const elapsedMs = (typeof performance !== "undefined" ? performance.now() : Date.now()) - startMs;
      try { cap.tick(lastState, baseSec + elapsedMs / 1000); } catch { /* best-effort; never break teardown */ }
      tailRafRef.current = requestAnimationFrame(push);
    };
    tailRafRef.current = requestAnimationFrame(push);
    tailTimerRef.current = setTimeout(() => {
      stopCaptureTail();
      finalizeOrAbortCapture("finish");
    }, CAPTURE_TAIL_MS);
  }

  function handleStop(stopSfx: boolean) {
    playCycleRef.current++; // invalidate any late-joining asset loaders from this run
    cancelAnimationFrame(rafRef.current);
    // stopSfx=true → real user pause: abort+discard immediately. Natural end → finish; but for
    // a PHOTO capture, first run a ~2s tail so the music "button" gets RECORDED (it already
    // plays live — the capture used to stop the instant the voice ended). Deck finishes
    // immediately (it has its own end on the last slide).
    if (stopSfx) {
      stopCaptureTail();
      finalizeOrAbortCapture("abort");
    } else if (captureRef.current && captureArmedRef.current && (!isDeck || aspect === "9:16") && lastCaptureStateRef.current) {
      runCaptureTail(captureRef.current, lastCaptureStateRef.current);
    } else {
      finalizeOrAbortCapture("finish");
    }
    bleeperRef.current?.stop();
    lipRef.current?.setDuck(1.0);

    // Clear any pending music tail first so we never leak a timer or double-fade.
    if (musicTailRef.current !== null) {
      clearTimeout(musicTailRef.current);
      musicTailRef.current = null;
    }

    if (stopSfx) {
      // User pause: kill everything immediately — music fades now, sfx stops now.
      musicBedRef.current?.stopWithFadeOut();
      sfxRef.current?.stopAll();
    } else {
      // Natural end: leave the scheduled stinger ringing (no stopAll), and let the
      // music play a ~2s "button" before fading out.
      musicTailRef.current = setTimeout(() => {
        musicBedRef.current?.stopWithFadeOut();
        musicTailRef.current = null;
      }, MUSIC_TAIL_MS);
    }
  }

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play(); else a.pause();
  }
  function seek(to: number) {
    const a = audioRef.current;
    if (!a) return;
    // A seek breaks the "uninterrupted first playthrough" → abort + discard the capture
    // (and cancel any in-flight post-end music tail).
    stopCaptureTail();
    finalizeOrAbortCapture("abort");
    a.currentTime = to;
    setCurrentTime(to);
  }

  // ── Arm a fresh CaptureController from the persistent audio tap. Called from
  //    both the first-play arming block (via handlePlay) and exportVideo(). The
  //    claim (captureArmedRef = true) is set synchronously before the first await
  //    so no concurrent call can double-arm. ──
  async function armCaptureController() {
    if (!audioTapRef.current || !lipRef.current) return;
    captureDoneRef.current = false;    // reset so export can re-arm after a prior capture
    captureArmedRef.current = true;    // synchronous claim — prevents double-arm
    const tap = audioTapRef.current;
    const ctx = lipRef.current.context;
    try {
      const tier = detectCaptureTier();
      if (!tier) { captureArmedRef.current = false; onCaptureStatusChange?.("aborted"); return; }
      const dims = tier === "mediarecorder" ? await probeExportDims(aspect) : aspectDims(aspect);
      const stageEl = stageRef.current;
      const stageH = stageEl?.getBoundingClientRect().height || dims.h;
      const cs = stageEl ? getComputedStyle(stageEl) : null;
      const baseFont = cs ? parseFloat(cs.fontSize) || 16 : 16;
      const family = cs?.fontFamily || "sans-serif";
      // Use cloneTrack() so each capture run gets a fresh, independent track clone.
      const audioTrack = tap.cloneTrack();
      // Match the canvas captions to the loaded web font (Geist) before compositing.
      try { await (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* font API absent */ }
      const ctrl = await CaptureController.create({
        dims,
        tier,
        mimeType: tier === "mediarecorder" ? (pickMp4Mime() ?? undefined) : undefined,
        // 16:9 caption is 1.32x the base (mirrors Captions.tsx); baseFont already scales
        // with the stage (stage font-size = min(16px, …cqh)), so the export tracks the preview.
        captionFont: { sizePx: baseFont * (dims.h / stageH) * (aspect === "16:9" ? 1.32 : 1), family },
        captionMask,
        bleepMode,
        audioTrack,
        audioSampleRate: ctx.sampleRate,
        audioChannels: audioTrack?.getSettings?.().channelCount ?? 2,
        backdropScale: dims.h / stageH,
      });
      // The run may have died while create() was in flight (pause/cancel/unmount
      // cleared the armed claim): a controller we can't own must be aborted on the
      // spot — else it records frozen frames with a held wake lock, forever.
      if (!captureArmedRef.current) { ctrl?.abort(); return; }
      if (ctrl) {
        captureRef.current = ctrl;
        captureDimsRef.current = dims;
        // Raise Rive drawing surface to at least the export resolution.
        try { charRef.current?.setCaptureResolution(dims); } catch { /* ignore */ }
        onCaptureStatusChange?.("recording");
        acquireWakeLock();
      } else {
        captureArmedRef.current = false; // codec unsupported — nothing to finalize
        onCaptureStatusChange?.("aborted");
      }
    } catch { captureRef.current = null; captureArmedRef.current = false; onCaptureStatusChange?.("aborted"); }
  }

  useEffect(() => () => {                          // Fix 4: release AudioContext on unmount
    cancelAnimationFrame(rafRef.current);
    cancelAnimationFrame(tailRafRef.current);
    if (controlsPeekTimerRef.current !== null) { clearTimeout(controlsPeekTimerRef.current); controlsPeekTimerRef.current = null; }
    if (tailTimerRef.current !== null) { clearTimeout(tailTimerRef.current); tailTimerRef.current = null; }
    if (musicTailRef.current !== null) {
      clearTimeout(musicTailRef.current);
      musicTailRef.current = null;
    }
    // Mark the run dead BEFORE releasing: unmount fires no pause event, so
    // finalizeOrAbortCapture never runs — a wake-lock request (or arm) still in
    // flight must see the run as dead and release/discard on the spot, not claim
    // into this dead component's refs.
    captureArmedRef.current = false;
    releaseWakeLock();                // don't hold the screen on past the player's own life
    captureRef.current?.abort();      // discard any in-flight capture
    audioTapRef.current?.dispose();   // disconnect the capture bus (graph is going away)
    lipRef.current?.dispose?.();
    musicBedRef.current?.dispose();   // stops music immediately; pending tail already cleared → no double-fade
    sfxRef.current?.stopAll();
  }, []);

  useEffect(() => {
    debugEnabledRef.current = debug;
    const img = imgRef.current;
    if (img) {
      // Only adjust the RESTING fit; an active zoom already forces contain itself.
      const zoomActive = !!img.style.transform && img.style.transform !== "none";
      if (!zoomActive) img.style.objectFit = "contain"; // baseline is always extents
    }
    if (debug) positionDebugLayer();
  }, [debug]);

  useEffect(() => {
    function onResize() { if (debugEnabledRef.current) positionDebugLayer(); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // In deck mode, the backdrop/main image follows the slide schedule; in photo mode it is the single imageUrl.
  const bg = isDeck ? deckImageUrl : imageUrl;

  // Always delegate to the latest render-scope impl (defeats stale-closure capture
  // of `aspect` etc. while keeping the handle object stable).
  const exportVideoImpl = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (detectCaptureTier() === null) { onCaptureStatusChange?.("unsupported"); return; }
    if (captureArmedRef.current) return; // already capturing — don't double-arm
    try {
      if (!lipRef.current || !audioTapRef.current) {
        // No audio graph yet (never played this mount): start playback. With
        // captureFirstPlay set, handlePlay auto-arms the capture from t=0.
        void audioEl.play();
        return;
      }
      // Graph exists (replay / interrupted first play): rewind WITHOUT seek()
      // (seek() aborts the capture we're arming), then arm explicitly, then play.
      audioEl.currentTime = 0;
      setCurrentTime(0);
      void (async () => {
        await armCaptureController();
        if (!captureArmedRef.current) return; // cancelled (or arm failed) mid-arm — don't ghost-restart audio
        try { await audioEl.play(); } catch { /* autoplay / user-gesture edge */ }
      })();
    } catch { /* never break playback */ }
  };
  const cancelExportImpl = () => {
    // During the post-end music tail the audio is already paused, so pause() fires no event
    // (handleStop never runs). Abort the tail + capture DIRECTLY so a cancelled export never
    // finalizes/delivers a blob. (Normal mid-playback cancel: no tail → pause routes
    // onPause → handleStop(true) → abort as before.)
    if (tailTimerRef.current !== null) {
      stopCaptureTail();
      finalizeOrAbortCapture("abort");
    }
    try { audioRef.current?.pause(); } catch { /* ignore */ }
  };
  const exportVideoRef = useRef(exportVideoImpl);
  const cancelExportRef = useRef(cancelExportImpl);
  exportVideoRef.current = exportVideoImpl;
  cancelExportRef.current = cancelExportImpl;
  useImperativeHandle(handleRef, () => ({
    exportVideo: () => exportVideoRef.current(),
    cancelExport: () => cancelExportRef.current(),
  }), []);

  return (
    <div
      className="grid h-full w-full place-items-center overflow-hidden bg-black"
      style={{ containerType: "size" }}
    >
      <div
        ref={stageRef}
        data-testid="player-stage"
        className="group relative overflow-hidden bg-black"
        style={stageStyle(aspect)}
        // Touch/pen taps peek the controls (mouse keeps pure hover-reveal).
        onPointerDown={(e) => { if (e.pointerType !== "mouse") peekControls(); }}
      >
        {/* Fix 3: blurred backdrop fills letterbox bars behind the zoomed/contained photo */}
        {bg && (
          <img
            src={bg}
            alt=""
            aria-hidden="true"
            data-testid="photo-backdrop"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "blur(24px) brightness(0.4)", transform: "scale(1.1)" }}
          />
        )}
        {bg && (
          <img
            ref={imgRef}
            src={bg}
            alt=""
            data-testid="main-photo"
            className="absolute inset-0 h-full w-full object-contain"
            style={{ transition: "none" }}
            onLoad={positionDebugLayer}
          />
        )}
        {isDeck && outgoingDeckUrl && (
          <img
            key={outgoingDeckUrl}
            src={outgoingDeckUrl}
            alt=""
            aria-hidden="true"
            data-testid="deck-slide-out"
            className="absolute inset-0 h-full w-full object-contain deck-slide-out"
            onAnimationEnd={() => setOutgoingDeckUrl(null)}
          />
        )}
        {debug && imageUrl && detectedObjects.length > 0 && (
          <DebugBoxes ref={debugRef} objects={detectedObjects} activeIndex={debugActiveIndex} />
        )}
        {debug && isDeck && deckImageUrl && deckDebugRegions.length > 0 && (
          <DebugBoxes ref={debugRef} objects={deckDebugRegions} activeIndex={-1} />
        )}
        {doodleImageUrl && (
          <img
            ref={doodleRef}
            src={doodleImageUrl}
            alt=""
            data-testid="doodle-overlay"
            className="absolute inset-0 h-full w-full object-contain pointer-events-none"
            style={{
              opacity: doodleRevealed ? 1 : 0,
              transition: "opacity 0.6s ease-in-out",
            }}
          />
        )}
        {isDeck && deckDoodleUrl && (
          <img
            key={deckDoodleUrl}
            ref={deckDoodleRef}
            src={deckDoodleUrl}
            alt=""
            decoding="async"
            data-testid="deck-doodle-overlay"
            className="absolute inset-0 h-full w-full object-contain pointer-events-none"
            style={{
              opacity: deckDoodleRevealed ? 1 : 0,
              transition: "opacity 0.6s ease-in-out",
            }}
          />
        )}
        <div
          data-testid="character-stage"
          className="pointer-events-none absolute inset-0"
        >
          {/* Decks render in a 16:9 stage → load the WIDE roast artboard (mrroast_wide,
              authored into the same mr_roast.riv) which fills 16:9; portrait photos use the
              full-frame 9:16 artboard. The key includes mode + wide so useRive (which binds
              its artboard once at mount) remounts if either changes. */}
          <RoastCharacter
            key={`${mode}-${isDeck && aspect === "16:9" ? "wide" : "tall"}`}
            ref={charRef}
            src={riveSrc}
            mode={mode}
            wide={isDeck && aspect === "16:9"}
          />
        </div>
        <Captions words={visibleWords} mode={mode} highlightActive={captionHighlight} aspect={aspect} captionMask={captionMask} bleepMode={bleepMode} />
        {/* Auto-hide controls while playing; reveal on hover (hover-capable devices only —
            touch taps make :hover stick and used to pin the controls over the captions),
            on a touch peek, or when paused/ended. */}
        <div
          className={`transition-opacity duration-300 ${
            isPlaying && !controlsPeek
              ? "opacity-0 pointer-events-none [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-hover:pointer-events-auto"
              : "opacity-100"
          }`}
        >
          <PlayerControls
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            mode={mode}
            onToggle={togglePlay}
            onSeek={seek}
            fullscreen={fullscreen}
            onToggleFullscreen={onToggleFullscreen}
          />
        </div>
        <audio
          ref={audioRef}
          src={audioUrl}
          autoPlay={autoPlay}
          className="hidden"
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onPlay={() => { setIsPlaying(true); void handlePlay(); }}
          // Chrome fires `pause` (with ended=true) AND `ended` at natural end. An
          // ended-pause is NOT a user pause: treat it as a natural end so the
          // completion stinger rings out (and the music tail runs) instead of being
          // cut by stopAll. Only a real user pause (not ended) stops the SFX now.
          onPause={(e) => { setIsPlaying(false); handleStop(!e.currentTarget.ended); }}
          onEnded={() => { setIsPlaying(false); handleStop(false); }}
        />
      </div>
    </div>
  );
});
RoastPlayer.displayName = "RoastPlayer";
